import { z } from 'zod';
import { objectIdSchema } from './common.js';
import { apiScopeSchema, apiWebhookEventSchema } from './public-api.js';

/**
 * DTOs for the app's own Settings → Integrations screen.
 *
 * Deliberately separate from `public-api.ts`. Those are a contract with
 * software we do not control and may only change additively; these are the
 * app talking to its own UI and are free to change with it. The screen shows
 * things the public API never exposes — a key's prefix, a subscription's
 * failure run, who created it — so sharing one DTO would have meant either
 * leaking that to integrators or hiding it from the person who owns it.
 */

/**
 * Two presets rather than four checkboxes.
 *
 * The person creating a key is pasting it into Zapier, not reasoning about
 * scopes, and a scope picker in a settings screen is how everybody ends up with
 * full access. "Read only" is genuinely useful — a reporting connector that
 * cannot cancel anybody's booking — and is the default for that reason.
 */
export const API_KEY_ACCESS = ['read_only', 'full'] as const;
export const apiKeyAccessSchema = z.enum(API_KEY_ACCESS);
export type ApiKeyAccess = z.infer<typeof apiKeyAccessSchema>;

export const createApiKeyInputSchema = z.object({
  /** What it is for, in the host's words: "Zapier", "our booking dashboard". */
  name: z.string().trim().min(1).max(60),
  access: apiKeyAccessSchema.default('read_only'),
});
export type CreateApiKeyInput = z.infer<typeof createApiKeyInputSchema>;

export const apiKeySchema = z.object({
  id: z.string(),
  name: z.string(),
  /** First 8 characters of the secret, so a key can be told from another. */
  prefix: z.string(),
  scopes: z.array(apiScopeSchema),
  access: apiKeyAccessSchema,
  lastUsedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type ApiKeyView = z.infer<typeof apiKeySchema>;

/** The one response that carries the secret. It is never returned again. */
export const apiKeyCreatedSchema = apiKeySchema.extend({ token: z.string() });
export type ApiKeyCreated = z.infer<typeof apiKeyCreatedSchema>;

export const apiKeyListSchema = z.object({ items: z.array(apiKeySchema) });

export const webhookAttemptSchema = z.object({
  at: z.string().datetime(),
  statusCode: z.number().int().nullable(),
  error: z.string().nullable(),
});

export const webhookViewSchema = z.object({
  id: z.string(),
  url: z.string(),
  events: z.array(z.union([apiWebhookEventSchema, z.literal('*')])),
  description: z.string().nullable(),
  status: z.enum(['active', 'disabled']),
  /**
   * `api_key`, or `oauth:<clientId>`. The UI turns the second into a platform
   * name, so a host can see that Zapier — not they — created this.
   */
  createdVia: z.string(),
  consecutiveFailures: z.number().int(),
  lastSuccessAt: z.string().datetime().nullable(),
  disabledAt: z.string().datetime().nullable(),
  /** Enough to tell a 404 from a timeout without a log. */
  lastAttempt: webhookAttemptSchema.nullable(),
  createdAt: z.string().datetime(),
});
export type WebhookView = z.infer<typeof webhookViewSchema>;

export const webhookListSchema = z.object({ items: z.array(webhookViewSchema) });

export const integrationIdParams = z.object({ id: objectIdSchema });
